import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, forwardRef, Inject, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith } from 'rxjs';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { ToastService } from 'src/app/services/toast.service';
import { SelectionConfig } from 'src/app/shared/class/Select';
import { ISelection } from 'src/app/shared/interfaces/ISelect';
import { IRetrieveParam } from 'src/app/shared/interfaces/ITable';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule, ReactiveFormsModule, FormsModule, TranslateModule, IconModule],
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ]
})
export class AutocompleteComponent implements OnInit, ControlValueAccessor, OnChanges {
  @ViewChild('auto') autocomplete!: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @Input() dataSource: ISelection[] = [];
  @Input() selectionConfig: SelectionConfig = {};
  @Input() appearance?: 'outline' | 'fill';
  @Input() placeholder = '';
  @Input() inputChanged?: (value: any) => void;
  @Input() blur?: () => void;
  @Input() focus?: () => void;
  @Input() onOpen?: () => void;
  @Input() fetchData?: (params: IRetrieveParam) => Observable<any>;
  @Input() params: IRetrieveParam = { page: 1, pageSize: 1000, sortText: '', globalSearch: '' };
  @Input() mapApiResponse?: (response: any) => any;
  @Input() class = '';
  @Input() defaultValue?: string;
  @Input() disabled = false;
  control: FormControl;
  filterInput: FormControl = new FormControl('');
  filteredOptions: Observable<ISelection[]>;
  loading = false;
  inputValue: any = '';
  value = '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (value: any) => { };
  private onTouched = () => { };
  constructor(
    @Inject(Injector) private injector: Injector,
    private translate: TranslateService,
    private toastService: ToastService) { }

  ngOnInit() {
    this.setFormControl();
    if (!this.fetchData) {
      this.filteredOptions = this.filterInput.valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value || ''))
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fetchData']) {
      this.retrieveData();
    }
    if (changes['defaultValue'] && this.defaultValue) {
      this.inputValue = this.dataSource.find((e) => e.value == this.defaultValue || e.text == this.defaultValue)?.text;
    }
  }

  writeValue(value: any) {
    if (value == null) {
      this.inputValue = ''; this.filterInput?.setValue('', { emitEvent: false });
      return;
    }
    const option = this.dataSource.find((e) => e.value == value);
    this.inputValue = option?.text ?? '';
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  private _filter(value: string): ISelection[] {
    const filterValue = value?.toLowerCase() ?? '';
    return this.dataSource.filter((option) => (option?.text ?? '').toLowerCase().includes(filterValue))
                          .sort((a, b) => (a.text ?? '').localeCompare(b.text ?? '', undefined, { sensitivity: 'accent' }));
  }

  setFormControl() {
    try {
      const formControl = this.injector.get(NgControl);
      switch (formControl.constructor) {
        case FormControlName:
          this.control = this.injector.get(FormGroupDirective).getControl(formControl as FormControlName);
          break;
        default:
          this.control = (formControl as FormControlDirective).form as FormControl;
          break;
      }
    } catch (err) {
      console.error(err);
      // this.control = new FormControl();
    }
  }

  setDisabled(option: ISelection): boolean {
    return (
      (typeof this.selectionConfig?.disabled === 'boolean'
        ? this.selectionConfig?.disabled
        : this.selectionConfig.disabled && this.selectionConfig.disabled(option)) ?? false
    );
  }

  onSelectionChange(event: any) {
    const selectedOption = this.dataSource.find((option) => option.text === event.option.value);
    this.onChange(selectedOption?.value);
    this.inputChanged && this.inputChanged({ inputValue: selectedOption?.text, value: selectedOption?.value });
  }

  onCancel(event: any) {
    if (!event.target.value && this.control) {
      this.control.setValue(null);
    }
  }

  hasError(): boolean {
    if (this.selectionConfig.optional && this.inputValue) {
      return this?.control?.invalid;
    } else {
      return this?.control?.invalid && this?.control?.touched;
    }
  }

  onBlur() {
    this.blur && this.blur();
    // after blur, if the input value is not in the data source, reset the input value and the form control
    const selectedOption = this.dataSource.find((option) => option.text === this.inputValue);
    if (!selectedOption) {
      this.inputValue = '';
      this.control?.setValue(null);
    }
    this.control?.markAsTouched();
  }

  onFocus() {
    this.focus && this.focus();
    // Notify parent to close other dropdowns when this one opens
    this.onOpen && this.onOpen();
    if (!this.inputValue) {
      this.filterInput.setValue('');
      this.autocomplete.options.forEach(option => option.deselect());
    }
  }

  onInputChanged(event: any) {
    const value = event.target.value.trim();
    const result = this.dataSource.find((option) => option.text && value && option.text.toLowerCase() === value.toLowerCase());
    if (this.selectionConfig.optional && !result) {
      this.onChange(value);
    } else {
      this.onChange(result?.value);
    }
    this.filterInput.setValue(value);
    this.inputChanged && this.inputChanged({ inputValue: value, value: result?.value });
  }

  retrieveData() {
    if (this.fetchData) {
      const reqParams = { ...this.params };
      this.dataSource = [];
      this.loading = true;
      this.fetchData(reqParams).subscribe({
        next: (response) => {
          let dataMapApi;
          if (this.mapApiResponse) {
            dataMapApi = this.mapApiResponse(response.data ?? response);
          }
          this.dataSource = (dataMapApi ?? response?.data ?? response ?? []).map((apiData: any) => ({
            value: apiData[this.selectionConfig.key || ''],
            text: apiData[this.selectionConfig.title || ''],
            group: apiData[this.selectionConfig.group || ''],
            disabled:
              typeof this.selectionConfig?.disabled === 'boolean'
                ? this.selectionConfig?.disabled
                : this.selectionConfig.disabled && this.selectionConfig.disabled(apiData)
          }));
          this.loading = false;
          this.filteredOptions = this.filterInput.valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value || ''))
          );
          if (this.control?.value) {
            this.inputValue = this.dataSource.find((e) => e.value == this.control.value)?.text;
          } else {
            // for autocomplete with out form control
            this.inputValue = this.dataSource.find((e) => e.value == this.defaultValue || e.text == this.defaultValue)?.text;
          }
        },
        error: ({ error }: HttpErrorResponse) => {
          this.toastService.error(this.translate.instant(error));
          this.loading = false;
        }
      });
    }
  }

  getClass(): string {
    return this.class;
  }

  clearInput(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.inputValue = '';
    this.value = '';
    this.filterInput.setValue('');

    // Clear mat-autocomplete selection
    if (this.autocomplete) {
      this.autocomplete.options.forEach(option => option.deselect());
    }

    // Update form control if it exists
    if (this.control) {
      this.control.setValue(null, { emitEvent: true });
      this.control.markAsTouched();
    }

    // Notify ControlValueAccessor
    this.onChange(null);
    this.onTouched();

    // Notify parent component if callback exists
    if (this.inputChanged) {
      this.inputChanged({ inputValue: '', value: null });
    }
  }

  closeDropdown = () => this.autocompleteTrigger && this.autocompleteTrigger.closePanel();

  toggleDropdown(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.autocompleteTrigger) {
      if (this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.closePanel();
      } else {
        this.autocompleteTrigger.openPanel();
      }
    }
  }

  get isOpen(): boolean {
    return this.autocompleteTrigger?.panelOpen ?? false;
  }
}
